import { z } from 'zod';
import type { useTranslations } from 'next-intl';

import {
  numberRegex,
  lowercaseRegex,
  uppercaseRegex,
  specialCharacterRegex,
} from '~/constants/regex';

export const fileSchema = z
  .object({
    url: z.string().optional(),
    signedId: z.string().optional(),
    filename: z.string().optional(),
    contentType: z.string().optional(),
  })
  .nullable();

const addPrefixHttpsIfNeeded = (val: unknown) => {
  if (typeof val === 'string' && val.trim() !== '') {
    const trimmedVal = val.trim();
    if (/^https?:\/\//i.test(trimmedVal)) {
      return trimmedVal;
    }

    return 'https://' + trimmedVal;
  }
  return '';
};

const verifyHostname = (val: string) => {
  try {
    const hostname = new URL(val).hostname;
    return /\.\w{2,}$/.test(hostname);
  } catch {
    return false;
  }
};

export function websiteSchema(t: ReturnType<typeof useTranslations>) {
  return z.preprocess(
    addPrefixHttpsIfNeeded,
    z.literal('').or(
      z
        .string()
        .url(t('validations.url'))
        .refine(verifyHostname, { message: t('validations.url') }),
    ),
  );
}

export const locationSchema = z.object({
  name: z.string(),
  country: z.string(),
  latitude: z.string(),
  longitude: z.string(),
  id: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
});

export function passwordSchema(t: ReturnType<typeof useTranslations>) {
  return z
    .string()
    .min(
      8,
      t('validations.minCharacter', {
        count: 8,
        field: t('fields.password'),
      }),
    )
    .max(
      15,
      t('validations.maxCharacter', {
        count: 15,
        field: t('fields.password'),
      }),
    )
    .regex(
      uppercaseRegex,
      t('validations.uppercase', {
        field: t('fields.password'),
      }),
    )
    .regex(
      lowercaseRegex,
      t('validations.lowercase', {
        field: t('fields.password'),
      }),
    )
    .regex(
      numberRegex,
      t('validations.containNumber', {
        field: t('fields.password'),
      }),
    )
    .regex(
      specialCharacterRegex,
      t('validations.containSpecialCharacter', {
        field: t('fields.password'),
      }),
    );
}

export function emailSchema(t: ReturnType<typeof useTranslations>) {
  return z
    .string()
    .min(
      1,
      t('validations.required', {
        field: t('fields.email'),
      }),
    )
    .email(t('validations.email'));
}

interface IPhoneSchemaOptionsProps {
  required?: boolean;
  phoneCodeField?: string;
  phoneNumberField?: string;
  phoneCountryField?: string;
}

export function phoneSchema(
  t: ReturnType<typeof useTranslations>,
  opts: IPhoneSchemaOptionsProps = {},
) {
  const {
    phoneCodeField,
    required = true,
    phoneNumberField,
    phoneCountryField,
  } = opts;

  if (!required) {
    return z.object({
      phoneCode: z.string(),
      phoneNumber: z.string(),
      phoneCountry: z.string(),
    });
  }

  return z.object({
    phoneCode: z.string().min(
      1,
      t('validations.required', {
        field: phoneCodeField || t('fields.callingCode'),
      }),
    ),
    phoneNumber: z.string().min(
      1,
      t('validations.required', {
        field: phoneNumberField || t('fields.phoneNumber'),
      }),
    ),
    phoneCountry: z.string().min(
      1,
      t('validations.required', {
        field: phoneCountryField || t('fields.countryCode'),
      }),
    ),
  });
}
